import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Upload, RefreshCw, AlertCircle, CheckCircle, Search,
  FileText, AlertTriangle, Trash2, Edit2, Save, X, Cloud
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';

/**
 * Admin Display Names Management
 * 
 * Manages human-readable track names (FR/EN) imported from CSV.
 * R2 = source of truth for audio files
 * MongoDB = editorial metadata (display names)
 */
const AdminDisplayNames = ({ t }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // Data state
  const [displayNames, setDisplayNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMissing, setFilterMissing] = useState(false);
  
  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editFr, setEditFr] = useState('');
  const [editEn, setEditEn] = useState('');
  
  // Messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [syncResult, setSyncResult] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const ADMIN_BASE = (API_URL || 'https://hearen-backend.onrender.com').replace(/\/$/, '');

  useEffect(() => {
    loadDisplayNames();
  }, []);

  const loadDisplayNames = async () => {
    setLoading(true);
    try {
      const url = `${ADMIN_BASE}/api/admin/display-names`;

      console.log('[AdminDisplayNames] Fetch URL:', url);

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setDisplayNames(data.display_names || []);
      }
    } catch (error) {
      console.error('Error loading display names:', error);
      setErrorMessage('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    setImportResult(null);
    setErrorMessage('');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${ADMIN_BASE}/api/admin/display-names/import-csv`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        setImportResult(result);
        setSuccessMessage(`Import réussi: ${result.imported} ajoutés, ${result.updated} mis à jour (version: ${result.metadata_version?.substring(0, 19) || 'unknown'})`);
        setTimeout(() => setSuccessMessage(''), 5000);
        
        // V2: Dispatch custom event to notify other tabs/windows about the update
        if (result.imported > 0 || result.updated > 0) {
          localStorage.setItem('hearen-library-updated', Date.now().toString());
          window.dispatchEvent(new CustomEvent('libraryUpdated'));
        }
        
        await loadDisplayNames();
      } else {
        const error = await response.json();
        setErrorMessage(error.detail || 'Erreur lors de l\'import');
      }
    } catch (error) {
      console.error('Error importing CSV:', error);
      setErrorMessage('Erreur lors de l\'import du CSV');
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSyncR2 = async () => {
    setSyncing(true);
    setSyncResult(null);
    setErrorMessage('');
    
    try {
      const response = await fetch(`${ADMIN_BASE}/api/admin/display-names/sync-r2`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const result = await response.json();
        setSyncResult(result);
        setSuccessMessage(`Sync R2 terminé: ${result.created} créés, ${result.marked_absent} marqués absents`);
        setTimeout(() => setSuccessMessage(''), 5000);
        
        // Notify other tabs/windows
        if (result.created > 0 || result.marked_absent > 0) {
          localStorage.setItem('hearen-library-updated', Date.now().toString());
          window.dispatchEvent(new CustomEvent('libraryUpdated'));
        }
        
        await loadDisplayNames();
      } else {
        const error = await response.json();
        setErrorMessage(error.detail || 'Erreur lors de la synchronisation R2');
      }
    } catch (error) {
      console.error('Error syncing R2:', error);
      setErrorMessage('Erreur lors de la synchronisation R2');
    } finally {
      setSyncing(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditFr(item.display_name_fr || '');
    setEditEn(item.display_name_en || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditFr('');
    setEditEn('');
  };

  const handleSaveEdit = async (trackId) => {
  try {
    const url = `${ADMIN_BASE}/api/admin/display-names/${trackId}`;
    console.log('[AdminDisplayNames] Save URL:', url);

    const response = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        display_name_fr: editFr,
        display_name_en: editEn
      })
    });

    if (response.ok) {
      const result = await response.json();
      // V2: Include metadata version in success message to confirm cache invalidation
      setSuccessMessage(`Mis à jour avec succès (version: ${result.metadata_version?.substring(0, 19) || 'unknown'})`);
      setTimeout(() => setSuccessMessage(''), 5000);
      setEditingId(null);
      setEditFr('');
      setEditEn('');

      // V2: Dispatch custom event to notify other tabs/windows
      localStorage.setItem('hearen-library-updated', Date.now().toString());
      window.dispatchEvent(new CustomEvent('libraryUpdated'));

      await loadDisplayNames();
    } else {
      let errorDetail = 'Erreur lors de la mise à jour';
      try {
        const error = await response.json();
        errorDetail = error.detail || errorDetail;
      } catch (_) {}
      setErrorMessage(errorDetail);
    }
  } catch (error) {
    console.error('Error saving edit:', error);
    setErrorMessage('Erreur lors de la mise à jour');
  }
};

  const handleDelete = async (trackId) => {
    if (!window.confirm(`Supprimer les noms pour "${trackId}" ?`)) return;
    
    try {
     const response = await fetch(`${ADMIN_BASE}/api/admin/display-names/${trackId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setSuccessMessage('Supprimé avec succès');
        setTimeout(() => setSuccessMessage(''), 3000);
        await loadDisplayNames();
      }
    } catch (error) {
      setErrorMessage('Erreur lors de la suppression');
    }
  };

  // Filter display names
  const filteredNames = displayNames.filter(item => {
    const matchesSearch = !searchTerm || 
      item.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.display_name_fr?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.display_name_en?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by missing = not present on R2
    const matchesMissing = !filterMissing || item.present_on_r2 === false;
    
    return matchesSearch && matchesMissing;
  });

  // Stats - present_on_r2 = true means present, false or undefined means missing
  const totalCount = displayNames.length;
  const presentCount = displayNames.filter(d => d.present_on_r2 === true).length;
  const missingCount = displayNames.filter(d => d.present_on_r2 === false).length;
  const autoGeneratedCount = displayNames.filter(d => d.auto_generated === true).length;

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8 pb-40">
      <div className="max-w-7xl mx-auto">
        <PageHeader t={t} />

        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-6 w-6" />
                  Gestion des Display Names V1
                </CardTitle>
                <CardDescription>
                  Noms humains des tracks (FR/EN) • R2 = source audio • MongoDB = métadonnées
                </CardDescription>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center gap-3 flex-wrap">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button
                  onClick={handleSyncR2}
                  disabled={syncing}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {syncing ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Sync R2...
                    </>
                  ) : (
                    <>
                      <Cloud className="h-4 w-4 mr-2" />
                      Sync with R2 now
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {importing ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Import en cours...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Importer CSV
                    </>
                  )}
                </Button>
                <Button
                  onClick={loadDisplayNames}
                  variant="outline"
                  disabled={loading}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Rafraîchir
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Info Alert */}
            <Alert className="border-blue-200 bg-blue-50">
              <FileText className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                <strong>Format CSV attendu :</strong>
                <code className="ml-2 bg-blue-100 px-2 py-0.5 rounded text-xs">
                  id,filename,display_name_fr,display_name_en
                </code>
                <br />
                <span className="text-sm">
                  Les fichiers absents de R2 seront marqués mais pas ignorés.
                </span>
              </AlertDescription>
            </Alert>

            {/* Import Result */}
            {importResult && (
              <Alert className={importResult.errors?.length > 0 ? 'border-yellow-300 bg-yellow-50' : 'border-green-300 bg-green-50'}>
                <CheckCircle className={`h-4 w-4 ${importResult.errors?.length > 0 ? 'text-yellow-600' : 'text-green-600'}`} />
                <AlertDescription className={importResult.errors?.length > 0 ? 'text-yellow-800' : 'text-green-800'}>
                  <strong>Résultat de l'import :</strong>
                  <ul className="mt-1 text-sm">
                    <li>✅ {importResult.imported} nouveaux ajoutés</li>
                    <li>🔄 {importResult.updated} mis à jour</li>
                    <li>⚠️ {importResult.ignored_missing} absents sur R2</li>
                    {importResult.errors?.length > 0 && (
                      <li className="text-red-600">❌ {importResult.errors.length} erreurs</li>
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* R2 Sync Result */}
            {syncResult && (
              <Alert className={syncResult.errors?.length > 0 ? 'border-yellow-300 bg-yellow-50' : 'border-blue-300 bg-blue-50'}>
                <Cloud className={`h-4 w-4 ${syncResult.errors?.length > 0 ? 'text-yellow-600' : 'text-blue-600'}`} />
                <AlertDescription className={syncResult.errors?.length > 0 ? 'text-yellow-800' : 'text-blue-800'}>
                  <strong>Résultat de la synchronisation R2 :</strong>
                  <ul className="mt-1 text-sm">
                    <li>📁 {syncResult.total_r2_tracks} tracks trouvés sur R2</li>
                    <li>✅ {syncResult.created} nouveaux créés (auto_generated)</li>
                    <li>👤 {syncResult.skipped_human_entries} entrées humaines préservées</li>
                    <li>🤖 {syncResult.skipped_auto_entries} entrées auto déjà présentes</li>
                    <li>✔️ {syncResult.marked_present} marqués présents</li>
                    <li>⚠️ {syncResult.marked_absent} marqués absents de R2</li>
                    {syncResult.errors?.length > 0 && (
                      <li className="text-red-600">❌ {syncResult.errors.length} erreurs</li>
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Messages */}
            {errorMessage && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}
            
            {successMessage && (
              <Alert className="bg-green-50 border-green-300">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">{successMessage}</AlertDescription>
              </Alert>
            )}

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-4 border text-center">
                <p className="text-3xl font-bold text-purple-600">{totalCount}</p>
                <p className="text-sm text-gray-500">Total noms</p>
              </div>
              <div className="bg-white rounded-lg p-4 border text-center">
                <p className="text-3xl font-bold text-green-600">{presentCount}</p>
                <p className="text-sm text-gray-500">Présents sur R2</p>
              </div>
              <div className="bg-white rounded-lg p-4 border text-center">
                <p className="text-3xl font-bold text-orange-500">{missingCount}</p>
                <p className="text-sm text-gray-500">Absents sur R2</p>
              </div>
            </div>

            {/* Search & Filter */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher par ID, filename ou nom..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filterMissing}
                  onChange={(e) => setFilterMissing(e.target.checked)}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-sm text-gray-600">
                  Afficher uniquement absents R2 ({missingCount})
                </span>
              </label>
            </div>

            {/* Table */}
            <div className="border rounded-lg overflow-hidden">
              {loading ? (
                <div className="p-8 text-center text-gray-500">
                  <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                  Chargement...
                </div>
              ) : filteredNames.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  {displayNames.length === 0 
                    ? "Aucun display name. Importez un CSV pour commencer."
                    : "Aucun résultat pour cette recherche."
                  }
                </div>
              ) : (
                <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b sticky top-0">
                      <tr>
                        <th className="px-3 py-3 text-left font-semibold text-gray-600">ID</th>
                        <th className="px-3 py-3 text-left font-semibold text-gray-600">Filename</th>
                        <th className="px-3 py-3 text-left font-semibold text-gray-600 min-w-[180px]">Nom FR</th>
                        <th className="px-3 py-3 text-left font-semibold text-gray-600 min-w-[180px]">Nom EN</th>
                        <th className="px-3 py-3 text-center font-semibold text-gray-600 w-24">Statut R2</th>
                        <th className="px-3 py-3 text-center font-semibold text-gray-600 w-24">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredNames.map((item) => (
                        <tr key={item.id} className={`${item.present_on_r2 === false ? 'bg-orange-50' : 'hover:bg-gray-50'}`}>
                          <td className="px-3 py-2">
                            <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                              {item.id}
                            </code>
                          </td>
                          <td className="px-3 py-2 text-gray-500 text-xs font-mono">
                            {item.filename || '-'}
                          </td>
                          <td className="px-3 py-2">
                            {editingId === item.id ? (
                              <input
                                type="text"
                                value={editFr}
                                onChange={(e) => setEditFr(e.target.value)}
                                className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                              />
                            ) : (
                              <span className="font-medium">{item.display_name_fr || '-'}</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {editingId === item.id ? (
                              <input
                                type="text"
                                value={editEn}
                                onChange={(e) => setEditEn(e.target.value)}
                                className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                              />
                            ) : (
                              <span className="font-medium">{item.display_name_en || '-'}</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {item.present_on_r2 === false ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs">
                                <AlertTriangle className="h-3 w-3" />
                                Absent
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                                <CheckCircle className="h-3 w-3" />
                                OK
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {editingId === item.id ? (
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => handleSaveEdit(item.id)}
                                  className="p-1 text-green-600 hover:bg-green-50 rounded"
                                  title="Sauvegarder"
                                >
                                  <Save className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                                  title="Annuler"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => handleEdit(item)}
                                  className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                  title="Modifier"
                                >
                                  <Edit2 className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(item.id)}
                                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                                  title="Supprimer"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="text-sm text-gray-500 text-center">
              Affichage de {filteredNames.length} sur {totalCount} entrées
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDisplayNames;
